import { Browser } from '@capacitor/browser';
import { trackOutboundClick } from '@/utils/analytics';

const ALLOWED_BROWSER_PROTOCOLS = ['http:', 'https:'];
const ALLOWED_EXTERNAL_PROTOCOLS = [...ALLOWED_BROWSER_PROTOCOLS, 'mailto:', 'tel:'];

function normalizeUrl(url, allowedProtocols) {
  if (typeof url !== 'string') return null;

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return null;

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return null;
    }
    return trimmedUrl;
  } catch (_) {
    return null;
  }
}

export function normalizeBrowserUrl(url) {
  return normalizeUrl(url, ALLOWED_BROWSER_PROTOCOLS);
}

export function normalizeExternalUrl(url) {
  return normalizeUrl(url, ALLOWED_EXTERNAL_PROTOCOLS);
}

/**
 * Helper function to open URLs with automatic Mixpanel tracking
 * Use this instead of Browser.open() directly
 *
 * @param {Object} options - Browser.open options
 * @param {string} options.url - The URL to open
 * @param {Object} metadata - Optional metadata for tracking
 * @returns {Promise}
 */
export const openUrl = async (options, metadata = {}) => {
  const url = normalizeBrowserUrl(options?.url);

  if (!url) {
    console.warn('[Browser] Skipping invalid URL', {
      source: metadata.source,
      page: metadata.page,
      title: metadata.title,
    });
    return null;
  }

  trackOutboundClick({
    url,
    linkType: 'in_app_browser_url',
    ...metadata,
  });

  // Open the browser
  const result = await Browser.open({ ...options, url });
  return result;
};

/**
 * Track and open external URL in system browser
 *
 * @param {string} url - The URL to open
 * @param {string} target - Window target
 * @param {Object} metadata - Optional metadata for tracking
 */
export const openExternal = (url, target = '_blank', metadata = {}) => {
  const normalizedUrl = normalizeExternalUrl(url);

  if (!normalizedUrl) {
    console.warn('[Browser] Skipping invalid external URL', {
      source: metadata.source,
      page: metadata.page,
      title: metadata.title,
    });
    return null;
  }

  trackOutboundClick({
    url: normalizedUrl,
    linkType: 'external_url',
    ...metadata,
  });

  // Open in system browser
  return window.open(normalizedUrl, target);
};
